import { SKILL_ONLY_FALLBACK_MESSAGE } from '@/user-skills/constants'

type SkillSelection = {
  selectedSkillIds?: string[] | null
  selectedPaxtonSkillNames?: string[] | null
}

type SkillNameSource = {
  id: string
  name: string
}

type SkillLibraryNames = {
  skills: readonly SkillNameSource[]
  paxtonSkills: readonly SkillNameSource[]
}

/** True when the conversation has at least one user or Paxton skill chip attached. */
export function conversationHasAttachedSkills(selection: SkillSelection | null | undefined): boolean {
  return Boolean(selection?.selectedSkillIds?.length || selection?.selectedPaxtonSkillNames?.length)
}

/** Display names for attached skills, in chip order (user skills, then Paxton skills). */
export function resolveAttachedSkillDisplayNames(selection: SkillSelection | null | undefined, library: SkillLibraryNames): string[] {
  const namesById = Object.fromEntries([...library.skills, ...library.paxtonSkills].map(({ id, name }) => [id, name]))
  return [...(selection?.selectedSkillIds ?? []), ...(selection?.selectedPaxtonSkillNames ?? [])].flatMap((id) => (namesById[id] ? [namesById[id]] : []))
}

/** Placeholder sent when the composer is empty and one or more skills are attached. */
export function formatSkillOnlyFallbackMessage(skillNames: readonly string[]): string {
  return `Use ${skillNames.join(', ')}`
}

/** True when `text` is the skill-only placeholder for the given attached skill names. */
export function isSkillOnlyFallbackMessage(text: string, attachedSkillNames: readonly string[] | null | undefined): boolean {
  if (!attachedSkillNames?.length) return false
  return text === formatSkillOnlyFallbackMessage(attachedSkillNames) || text === SKILL_ONLY_FALLBACK_MESSAGE
}

/**
 * Resolves the text that should be sent from the chat composer.
 *
 * Returns the trimmed user text when present. When the input is empty/whitespace-only and at least
 * one skill is attached, returns a named fallback (`Use {name}` / `Use {name}, {name}`) so the
 * backend accepts the turn. Returns null when there is nothing to send.
 *
 * `hasAttachedSkills` keeps send enabled when chips are attached but library names are not loaded
 * yet; in that rare case {@link SKILL_ONLY_FALLBACK_MESSAGE} (`Follow the attached skill.`) is used.
 */
export function resolveComposerSubmitText(
  inputText: string,
  attachedSkillNames: readonly string[],
  hasAttachedSkills = attachedSkillNames.length > 0
): string | null {
  const trimmed = inputText.trim()
  if (trimmed.length > 0) return trimmed
  if (attachedSkillNames.length > 0) return formatSkillOnlyFallbackMessage(attachedSkillNames)
  if (hasAttachedSkills) return SKILL_ONLY_FALLBACK_MESSAGE
  return null
}
